"use client";

import { createContext, useContext, useMemo, type ReactNode } from "react";

export const defaultMessages = {
  cancel: "取消",
  close: "关闭",
  closeNotification: "关闭通知",
  closePicker: (label: string) => `关闭${label}`,
  selectLabel: (label: string) => `选择${label}`,
  searchLabel: (label: string) => `搜索${label}`,
  searchOptions: "搜索选项",
  noOptions: "没有匹配的选项",
  copy: (label: string) => `复制${label}`,
  copied: (label: string) => `${label}已复制`,
  copyFailed: "复制失败，请选择内容后手动复制。",
  showValue: "显示内容",
  hideValue: "隐藏内容",
  required: "请填写或选择此项。",
  invalidEmail: "请输入有效的邮箱地址。",
  invalidUrl: "请输入有效的网址。",
  tooShort: (length: number) => `请至少输入 ${length} 个字符。`,
  tooLong: (length: number) => `请最多输入 ${length} 个字符。`,
  rangeUnderflow: (value: string) => `请输入不小于 ${value} 的数值。`,
  rangeOverflow: (value: string) => `请输入不大于 ${value} 的数值。`,
  invalidValue: "请检查此项的格式或取值。",
  formErrors: (count: number) => `有 ${count} 项内容需要修改`,
  confirmText: (value: string) => `输入 ${value} 确认`,
  preparing: "正在核对…",
  submitting: "正在提交…",
  retry: "重试",
  actionFailed: "操作未完成，请重试。",
  chooseTime: "选择时间或输入 08:15",
  invalidTime: "请输入有效时间，例如 08:15",
};

export type KitseedMessages = typeof defaultMessages;
export type KitseedDensity = "compact" | "comfortable";
type KitseedContextValue = {
  density: KitseedDensity;
  messages: KitseedMessages;
};
const KitseedContext = createContext<KitseedContextValue>({
  density: "compact",
  messages: defaultMessages,
});

/** Optional configuration. Nested providers inherit unspecified settings. */
export function KitseedProvider({
  children,
  density,
  messages,
}: {
  children: ReactNode;
  density?: KitseedDensity;
  messages?: Partial<KitseedMessages>;
}) {
  const parent = useContext(KitseedContext);
  const value = useMemo(
    () => ({
      density: density ?? parent.density,
      messages: { ...parent.messages, ...messages },
    }),
    [density, messages, parent],
  );
  return <KitseedContext.Provider value={value}>{children}</KitseedContext.Provider>;
}

export function useKitseed() {
  return useContext(KitseedContext);
}
